import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { AiGenerateReplyRequest, AiGenerateReplyResponse, GenerateParams } from '../../types/qna/aiReply';
import { SlackNotifyPayload } from '../../types/qna/slackNotify';
import { FeedbackData, FeedbackRating } from '../../types/qna/feedback';
import qnaToastMsg from '../../constants/service/toastMsg/qnaToastMsg';
import {
  AI_REPLY_API_TIMEOUT,
  SLACK_NOTIFY_TIMEOUT,
  AI_FEEDBACK_TIMEOUT,
} from '../../constants/service/app/csQnaConstants';

/**
 * AI 답변 생성 API 호출
 * @param params - 문의 정보 및 키워드
 * @returns 생성된 답변 문자열
 * @throws API 호출 실패 시 에러
 */
export const fetchAIGeneratedReply = async (params: GenerateParams): Promise<string> => {
  const requestBody: AiGenerateReplyRequest = params;

  const response = await axios.post<AiGenerateReplyResponse>('/api/ai-generate-reply', requestBody, {
    timeout: AI_REPLY_API_TIMEOUT,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || qnaToastMsg.AI_REPLY_SERVER_ERROR);
  }

  return response.data.reply || '';
};

/**
 * AI 답변 생성 에러 핸들링
 * @param err - 에러 객체
 * @returns 사용자에게 표시할 에러 메시지
 *
 * @example
 * - 클라이언트 타임아웃: qnaToastMsg.AI_REPLY_REQUEST_TIMEOUT
 * - 네트워크 오류: qnaToastMsg.AI_REPLY_NETWORK_ERROR
 * - API 서버 에러: 서버에서 받은 에러 메시지 또는 qnaToastMsg.AI_REPLY_SERVER_ERROR
 */
export const handleAIReplyError = (err: unknown): string => {
  // Axios 에러인 경우
  if (axios.isAxiosError(err)) {
    // 클라이언트 측 타임아웃 (요청이 서버에 도달하기 전에 시간 초과)
    if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
      return qnaToastMsg.AI_REPLY_REQUEST_TIMEOUT;
    }

    // 네트워크 에러 (서버 응답 없음)
    if (!err.response) {
      return qnaToastMsg.AI_REPLY_NETWORK_ERROR;
    }

    // 서버에서 전달한 에러 메시지 (API response.data.error)
    if (err.response?.data?.error) {
      return err.response.data.error;
    }

    // HTTP 상태 코드별 처리
    const statusCode = err.response?.status;
    if (statusCode === 504) {
      // Gateway Timeout (API 서버 내부 타임아웃)
      return qnaToastMsg.AI_REPLY_SERVER_ERROR;
    }

    // 그 외 Axios 에러 (기본 메시지 사용)
    return qnaToastMsg.AI_REPLY_SERVER_ERROR;
  }

  // Error 객체인 경우 (fetchAIGeneratedReply에서 throw한 에러)
  if (err instanceof Error) {
    return err.message;
  }

  // 알 수 없는 에러
  return qnaToastMsg.AI_REPLY_SERVER_ERROR;
};

/**
 * 슬랙 알림 전송 함수
 * AI 답변 전송 시 슬랙 워크플로우로 데이터를 전송합니다.
 * 실패 시 조용히 실패하여 사용자 경험에 영향을 주지 않습니다.
 *
 * @param data 슬랙 알림 페이로드
 *
 * @note
 * React Hook이 아닌 일반 함수로 구현한 이유:
 * - CsQnaDetailContainer가 클래스 컴포넌트라서 React Hook을 직접 사용할 수 없음
 * - 일반 유틸 함수로 만들면 함수형/클래스형 컴포넌트 모두에서 사용 가능
 * - 단순한 API 호출 로직이므로 Hook으로 만들 필요가 없음
 */
export async function sendSlackNotification(data: SlackNotifyPayload): Promise<void> {
  try {
    // timestamp 자동 추가 (한국 시간 기준)
    const payload = {
      ...data,
      timestamp: formatInTimeZone(new Date(), 'Asia/Seoul', 'yyyy-MM-dd HH:mm:ss'),
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SLACK_NOTIFY_TIMEOUT);

    const response = await fetch('/api/slack-notify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Slack notification failed: ${response.status}`);
    }
  } catch (error) {
    // 조용히 실패 (사용자 경험에 영향 없음)
    console.error('Failed to send Slack notification:', error);
  }
}

/**
 * 피드백 데이터를 구글 스프레드시트로 전송
 * 실패 시 조용히 실패하여 사용자 경험에 영향을 주지 않습니다.
 *
 * @param data 피드백 기본 데이터 (rating, timestamp 제외)
 * @param rating 피드백 평가 ('good' | 'bad' | 'none' | 'closed')
 */
export async function sendFeedbackToSheet(
  data: Omit<FeedbackData, 'feedback_rating' | 'timestamp'>,
  rating: FeedbackRating,
): Promise<void> {
  try {
    // timestamp 자동 추가 (한국 시간 기준)
    const payload: FeedbackData = {
      ...data,
      feedback_rating: rating,
      timestamp: formatInTimeZone(new Date(), 'Asia/Seoul', 'yyyy-MM-dd HH:mm:ss'),
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), AI_FEEDBACK_TIMEOUT);

    const response = await fetch('/api/ai-feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Feedback API failed: ${response.status}`);
    }
  } catch (error) {
    // 조용히 실패 (사용자 경험에 영향 없음)
    console.error('Failed to send feedback:', error);
  }
}
